import React, { useCallback, useEffect, useRef, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import CircularProgress from '@mui/material/CircularProgress';
import { phappsRequest } from '@/services/apiService';
import { API_PATHS } from '@/config/apiConstants';

// ── Helpers ───────────────────────────────────────────────────────────────────

const LOG_TYPE_MAP = {
  Error:  'error',
  Debug:  'debug',
  Access: 'access',
  App:    'app',
};

const detectLevel = (line) => {
  const u = line.toUpperCase();
  if (u.includes(' FATAL:') || u.includes('"LEVEL":60'))                   return 'fatal';
  if (u.includes(' ERROR:') || u.includes('"LEVEL":50'))                   return 'error';
  if (u.includes(' WARN:')  || u.includes('"LEVEL":40'))                   return 'warn';
  if (u.includes(' INFO:')  || u.includes('"LEVEL":30'))                   return 'info';
  if (u.includes(' DEBUG:') || u.includes('"LEVEL":20'))                   return 'debug';
  return 'default';
};

const LEVEL_COLORS = {
  fatal:   'text-red-300 font-bold',
  error:   'text-red-300',
  warn:    'text-yellow-300',
  info:    'text-emerald-300',
  debug:   'text-sky-300',
  default: 'text-slate-200',
};

// Highlight search term inside a log line (returns array of spans)
const highlightSearch = (text, search) => {
  if (!search) return text;
  const escaped = search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
  return parts.map((p, i) =>
    p.toLowerCase() === search.toLowerCase()
      ? <mark key={i} className="bg-yellow-300 text-gray-900 rounded-sm px-0.5">{p}</mark>
      : p
  );
};

// ── Component ─────────────────────────────────────────────────────────────────

const LogModal = ({ isOpen, onClose, logType }) => {
  const apiType  = LOG_TYPE_MAP[logType] || 'error';
  const title    = `${logType} Log`;
  const todayStr = new Date().toISOString().slice(0, 10); // YYYY-MM-DD

  // State
  const [lines,        setLines]        = useState([]);
  const [totalLines,   setTotalLines]   = useState(0);
  const [totalPages,   setTotalPages]   = useState(1);
  const [page,         setPage]         = useState(1);
  const [linesPerPage, setLinesPerPage] = useState(100);
  const [filename,     setFilename]     = useState('');

  const [date,         setDate]         = useState(todayStr);
  const [availDates,   setAvailDates]   = useState([]);
  const [datesLoading, setDatesLoading] = useState(false);

  const [search,       setSearch]       = useState('');
  const [activeSearch, setActiveSearch] = useState('');

  const [loading,      setLoading]      = useState(false);
  const [errorMsg,     setErrorMsg]     = useState('');
  const [autoRefresh,  setAutoRefresh]  = useState(false);

  const timerRef    = useRef(null);
  const bottomRef   = useRef(null);

  // ── Load available log dates ─────────────────────────────────────────────
  const loadDates = useCallback(async () => {
    setDatesLoading(true);
    try {
      const res = await phappsRequest(`${API_PATHS.LOG_DATES}?type=${apiType}`, 'GET');
      setAvailDates(res?.dates || []);
    } catch (_) {
      setAvailDates([]);
    }
    setDatesLoading(false);
  }, [apiType]);

  // ── Fetch log lines ──────────────────────────────────────────────────────
  const fetchLogs = useCallback(async (opts = {}) => {
    setLoading(true);
    setErrorMsg('');
    try {
      const d  = opts.date         ?? date;
      const p  = opts.page         ?? page;
      const lp = opts.linesPerPage ?? linesPerPage;
      const s  = opts.search       ?? activeSearch;

      const params = new URLSearchParams({
        type: apiType,
        page: String(p),
        linesPerPage: String(lp),
        date: d,
        ...(s ? { search: s } : {}),
      });

      const res = await phappsRequest(`${API_PATHS.LOGS}?${params}`, 'GET');

      if (!res?.exists) {
        setErrorMsg(res?.message || 'Log file not found.');
        setLines([]);
        setTotalLines(0);
        setTotalPages(1);
      } else {
        setLines(res.lines || []);
        setTotalLines(res.totalLines || 0);
        setTotalPages(res.totalPages || 1);
        setPage(res.currentPage || p);
        setFilename(res.filename || '');
      }
    } catch (e) {
      setErrorMsg(e?.message || 'Failed to fetch logs.');
      setLines([]);
    }
    setLoading(false);
  }, [apiType, date, page, linesPerPage, activeSearch]);

  // On open: reset state, load dates, fetch logs
  useEffect(() => {
    if (!isOpen) return;
    const today = new Date().toISOString().slice(0, 10);
    setPage(1);
    setDate(today);
    setSearch('');
    setActiveSearch('');
    setLines([]);
    setErrorMsg('');
    loadDates();
    fetchLogs({ page: 1, date: today, search: '' });
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch whenever page/date/linesPerPage/activeSearch change (after initial load)
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) { isFirstRender.current = false; return; }
    if (!isOpen) return;
    fetchLogs();
  }, [page, date, linesPerPage, activeSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresh every 30s on current log
  useEffect(() => {
    clearInterval(timerRef.current);
    if (autoRefresh && date === new Date().toISOString().slice(0, 10) && isOpen) {
      timerRef.current = setInterval(() => fetchLogs(), 30_000);
    }
    return () => clearInterval(timerRef.current);
  }, [autoRefresh, date, isOpen, fetchLogs]);

  // ── Handlers ────────────────────────────────────────────────────────────
  const handleSearch = (e) => {
    e.preventDefault();
    setActiveSearch(search.trim());
    setPage(1);
  };

  const clearSearch = () => {
    setSearch('');
    setActiveSearch('');
    setPage(1);
  };

  const handleDateChange = (d) => {
    setDate(d);
    setPage(1);
    setAutoRefresh(false);
  };

  const handleLinesPerPage = (lp) => {
    setLinesPerPage(lp);
    setPage(1);
  };

  const handlePage = (p) => {
    if (p >= 1 && p <= totalPages) setPage(p);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-gray-800 rounded-2xl shadow-2xl w-full max-w-5xl flex flex-col h-[90vh] border border-gray-600">

        {/* ── Header ──────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-600 bg-gray-750 rounded-t-2xl flex-shrink-0" style={{ backgroundColor: '#2d3748' }}>
          <div className="flex items-center gap-3">
            <h2 className="text-base font-bold text-white">{title}</h2>
            {loading && <CircularProgress size={14} sx={{ color: '#38bdf8' }} />}
            {autoRefresh && date === new Date().toISOString().slice(0, 10) && (
              <span className="text-[10px] font-semibold text-emerald-300 animate-pulse">● LIVE</span>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition p-1 rounded">
            <CloseIcon style={{ fontSize: 20 }} />
          </button>
        </div>

        {/* ── Controls ────────────────────────────────────────────────── */}
        <div className="flex flex-wrap items-center gap-3 px-5 py-3 border-b border-gray-600 flex-shrink-0" style={{ backgroundColor: '#2d3748' }}>

          {/* Date picker */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-300 uppercase tracking-wide font-semibold">Date</span>
            <select
              value={date}
              onChange={(e) => handleDateChange(e.target.value)}
              disabled={datesLoading}
              className="bg-gray-600 border border-gray-500 text-white text-xs rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-sky-400"
            >
              {availDates.length === 0 && <option value={todayStr}>{todayStr}</option>}
              {availDates.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>

          {/* Lines per page */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-300 uppercase tracking-wide font-semibold">Lines</span>
            <select
              value={linesPerPage}
              onChange={(e) => handleLinesPerPage(Number(e.target.value))}
              className="bg-gray-600 border border-gray-500 text-white text-xs rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-sky-400"
            >
              {[50, 100, 200, 500].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>

          {/* Search */}
          <form onSubmit={handleSearch} className="flex items-center gap-2">
            <div className="relative">
              <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" style={{ fontSize: 14 }} />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Filter lines…"
                className="bg-gray-600 border border-gray-500 text-white text-xs rounded-lg pl-7 pr-7 py-1.5 w-48 focus:outline-none focus:ring-1 focus:ring-sky-400 placeholder:text-gray-400"
              />
              {search && (
                <button type="button" onClick={clearSearch}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition">
                  <CloseIcon style={{ fontSize: 12 }} />
                </button>
              )}
            </div>
            <button type="submit"
              className="px-2.5 py-1.5 bg-sky-500 text-white text-xs font-semibold rounded-lg hover:bg-sky-400 transition">
              Search
            </button>
          </form>

          {/* Right controls */}
          <div className="flex items-center gap-2 ml-auto">
            <label className="flex items-center gap-1.5 text-xs text-gray-300 cursor-pointer select-none" title={date !== 'current' ? 'Only available on current log' : ''}>
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                disabled={date !== new Date().toISOString().slice(0, 10)}
                className="w-3 h-3 accent-sky-400"
              />
              Auto 30s
            </label>
            <button onClick={() => fetchLogs()}
              className="flex items-center gap-1 px-2.5 py-1.5 border border-gray-500 text-gray-300 text-xs rounded-lg hover:bg-gray-600 hover:text-white transition">
              <RefreshIcon style={{ fontSize: 13 }} /> Refresh
            </button>
          </div>
        </div>

        {/* ── Stats bar ───────────────────────────────────────────────── */}
        <div className="flex items-center gap-4 px-5 py-1.5 border-b border-gray-600 flex-shrink-0 text-[11px] text-gray-300" style={{ backgroundColor: '#374151' }}>
          {filename && (
            <span className="font-mono text-sky-300 font-semibold">{filename}</span>
          )}
          <span><span className="text-white font-semibold">{totalLines.toLocaleString()}</span> {activeSearch ? 'matched' : 'total'} lines</span>
          <span>Page <span className="text-white font-semibold">{page}</span> / <span className="text-white font-semibold">{totalPages}</span></span>
          {activeSearch && (
            <span className="text-yellow-300 font-semibold">filter: "{activeSearch}"</span>
          )}
        </div>

        {/* ── Log output ──────────────────────────────────────────────── */}
        <div className="flex-1 min-h-0 overflow-auto px-5 py-4 font-mono text-xs" style={{ backgroundColor: '#1e2433' }}>
          {loading && lines.length === 0 && (
            <div className="text-gray-400 italic">Loading…</div>
          )}
          {errorMsg && (
            <div className="text-red-300 italic">{errorMsg}</div>
          )}
          {!loading && !errorMsg && lines.length === 0 && (
            <div className="text-gray-400 italic">
              {activeSearch ? `No lines match "${activeSearch}".` : 'No entries found.'}
            </div>
          )}
          {lines.map((line, i) => {
            const level = detectLevel(line);
            return (
              <div
                key={i}
                className={`${LEVEL_COLORS[level]} leading-relaxed mb-1 whitespace-pre-wrap break-all border-b pb-1 last:border-0 last:pb-0`}
                style={{ borderColor: '#2a3347' }}
              >
                {highlightSearch(line, activeSearch)}
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>

        {/* ── Pagination footer ────────────────────────────────────────── */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-gray-600 flex-shrink-0 rounded-b-2xl" style={{ backgroundColor: '#2d3748' }}>
          <div className="flex items-center gap-1">
            <button
              onClick={() => handlePage(1)}
              disabled={page <= 1 || loading}
              className="px-2 py-1 text-xs bg-gray-600 text-gray-200 rounded disabled:opacity-30 hover:bg-gray-500 hover:text-white transition"
            >«</button>
            <button
              onClick={() => handlePage(page - 1)}
              disabled={page <= 1 || loading}
              className="px-2 py-1 text-xs bg-gray-600 text-gray-200 rounded disabled:opacity-30 hover:bg-gray-500 hover:text-white transition"
            >‹</button>
            <span className="text-xs text-gray-300 px-2">
              Page <span className="text-white font-semibold">{page}</span> of <span className="text-white font-semibold">{totalPages}</span>
            </span>
            <button
              onClick={() => handlePage(page + 1)}
              disabled={page >= totalPages || loading}
              className="px-2 py-1 text-xs bg-gray-600 text-gray-200 rounded disabled:opacity-30 hover:bg-gray-500 hover:text-white transition"
            >›</button>
            <button
              onClick={() => handlePage(totalPages)}
              disabled={page >= totalPages || loading}
              className="px-2 py-1 text-xs bg-gray-600 text-gray-200 rounded disabled:opacity-30 hover:bg-gray-500 hover:text-white transition"
            >»</button>
          </div>
          <button onClick={onClose}
            className="px-4 py-1.5 bg-gray-600 text-gray-100 text-xs font-semibold rounded-lg hover:bg-gray-500 transition">
            Close
          </button>
        </div>

      </div>
    </div>
  );
};

export default LogModal;
